export const IMAGE_UPLOAD_LIMITS = Object.freeze({
  maxWidth: 1600,
  maxHeight: 1200,
  targetBytes: 1.5 * 1024 * 1024,
  maxSourceBytes: 30 * 1024 * 1024,
});

const supportedTypes = new Set(["image/jpeg", "image/png", "image/webp"]);

export function calculateImageSize(width, height, limits = IMAGE_UPLOAD_LIMITS) {
  const scale = Math.min(1, limits.maxWidth / width, limits.maxHeight / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    resized: scale < 1,
  };
}

export function formatImageBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function loadImage(file) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file, { imageOrientation: "from-image" });
  }

  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("تعذر قراءة الصورة. جرّب صورة أخرى."));
    };
    image.src = objectUrl;
  });
}

function canvasBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("تعذر تحسين الصورة."))),
      type,
      quality
    );
  });
}

function optimizedName(name, type) {
  const base = name.replace(/\.[^.]+$/, "") || "image";
  const extension = type === "image/webp" ? "webp" : type === "image/png" ? "png" : "jpg";
  return `${base}-optimized.${extension}`;
}

function optimizationDetails(original, uploaded, source, target) {
  const changed = original.size !== uploaded.size || source.width !== target.width;
  return {
    changed,
    originalBytes: original.size,
    uploadedBytes: uploaded.size,
    originalWidth: source.width,
    originalHeight: source.height,
    width: target.width,
    height: target.height,
    summary: changed
      ? `تم تحسين الصورة: ${source.width}×${source.height} (${formatImageBytes(original.size)}) → ${
          target.width
        }×${target.height} (${formatImageBytes(uploaded.size)})`
      : `الصورة مناسبة للرفع: ${target.width}×${target.height} (${formatImageBytes(
          uploaded.size
        )})`,
  };
}

export async function optimizeImageForUpload(file, limits = IMAGE_UPLOAD_LIMITS) {
  if (!supportedTypes.has(file.type)) {
    throw new Error("ارفع صورة بصيغة JPG أو PNG أو WebP.");
  }
  if (file.size <= 0 || file.size > limits.maxSourceBytes) {
    throw new Error("يجب ألا يتجاوز حجم الصورة الأصلية 30 ميغابايت.");
  }

  const source = await loadImage(file);
  try {
    const sourceWidth = source.width || source.naturalWidth;
    const sourceHeight = source.height || source.naturalHeight;
    if (!sourceWidth || !sourceHeight) throw new Error("أبعاد الصورة غير صالحة.");

    const target = calculateImageSize(sourceWidth, sourceHeight, limits);
    const sourceDimensions = { width: sourceWidth, height: sourceHeight };
    if (!target.resized && file.size <= limits.targetBytes) {
      return {
        file,
        optimization: optimizationDetails(file, file, sourceDimensions, target),
      };
    }

    const canvas = document.createElement("canvas");
    canvas.width = target.width;
    canvas.height = target.height;
    const context = canvas.getContext("2d", { alpha: true });
    if (!context) throw new Error("تعذر تجهيز الصورة للرفع.");
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(source, 0, 0, target.width, target.height);

    const outputType = "image/webp";
    let blob = await canvasBlob(canvas, outputType, 0.86);
    if (blob.size > limits.targetBytes) blob = await canvasBlob(canvas, outputType, 0.76);
    if (blob.size > limits.targetBytes) blob = await canvasBlob(canvas, outputType, 0.66);

    const optimized = new File([blob], optimizedName(file.name, blob.type), {
      type: blob.type,
      lastModified: file.lastModified,
    });
    return {
      file: optimized,
      optimization: optimizationDetails(file, optimized, sourceDimensions, target),
    };
  } finally {
    if (typeof source.close === "function") source.close();
  }
}
